import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'quill-mention';
import './mention.css'; // Make sure this highlights .mention properly

export default function MentionEditorEdit({ users, description, onChange, delta }) {
  const [value, setValue] = useState('');
  const quillRef = useRef(null);
  const previousDescriptionRef = useRef(description);
  const usersRef = useRef(users);
  
  // Update users ref without triggering re-render
  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  // Use a stable modules object that doesn't recreate on every users change
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['clean'],// This must match your custom handler
      ],
    },
    mention: {
      mentionDenotationChars: ['@'],
      source: function (searchTerm, renderList) {
        // Use usersRef.current to get the latest users without recreating modules
        const currentMentionValues = usersRef.current?.map((user) => ({
          id: user.id,
          value: user.full_name,
        })) || [];
        const matches = currentMentionValues.filter((item) =>
          item.value.toLowerCase().includes(searchTerm.toLowerCase())
        );
        renderList(matches, searchTerm);
      },
      renderItem(item) {
        return `<div><strong>${item.value}</strong></div>`;
      },
    },
  }), []); // Empty dependency array - modules is now stable

  // Load delta content or fallback to HTML only once
  useEffect(() => {
    // Skip if description hasn't changed from what we already have
    if (description === previousDescriptionRef.current && value) {
      return;
    }
    
    previousDescriptionRef.current = description;
    
    if (delta) {
      try {
        let parsedDelta = delta;
        // Only parse once if it's a string, avoid infinite parsing
        while (typeof parsedDelta === 'string') {
          parsedDelta = JSON.parse(parsedDelta);
        }
        const Quill = require('quill');
        const tempQuill = new Quill(document.createElement('div'));
        tempQuill.setContents(parsedDelta);
        const html = tempQuill.root.innerHTML;
        setValue(html);
      } catch (err) {
        console.error('Failed to parse delta:', err);
        // If delta parsing fails, fall back to description
        if (description) {
          setValue(description);
        }
      }
    } else if (description) {
      setValue(description);
    }
    // eslint-disable-next-line
  }, [delta, description]);


  const handleChange = (content, _delta, _source, editor) => {
    setValue(content);
    // Update the ref to track the latest content we're working with
    previousDescriptionRef.current = content;

    // Only process delta if there's actual content or mentions
    const quillDelta = editor.getContents(); // Delta object
    const ops = quillDelta.ops;

    // Check if content is effectively empty (only whitespace and formatting)
    const hasRealContent = ops.some(op => {
      if (typeof op.insert === 'string') {
        return op.insert.trim().length > 0;
      }
      return op.insert?.mention; // Has mentions
    });

    const mentions = [];
    let cursor = 0;

    ops.forEach(op => {
      if (typeof op.insert === 'string') {
        cursor += op.insert.length;
      } else if (op.insert?.mention) {
        const mentionText = "@" + op.insert.mention.value;
        mentions.push({
          id: op.insert.mention.id,
          value: op.insert.mention.value,
          mentionText,
          start: cursor,
          end: cursor + mentionText.length
        });
        cursor += mentionText.length;
      }
    });

    if (onChange) {
      // Only send delta if there's real content, otherwise send null
      const deltaToSend = hasRealContent ? quillDelta : null;
      
      onChange({
        description: content,     // HTML string
        delta: deltaToSend,       // Delta object or null
        mentions: mentions        // Array of mentions
      });
    }
  };

  
  return (
    <div className="mb-2">
      <div className="col-lg-12">
        <label className="text-label mb-2 d-block">Description</label>
        <div className="custom-quill-wrapper w-100">
          <ReactQuill
            ref={(el) => {
              if (quillRef.current !== el) {
                quillRef.current = el;
              }
            }}
            theme="snow"
            value={value || ''}
            onChange={handleChange}
            modules={modules}
            style={{ minHeight: '200px' }}
            readOnly={false}
          />
        </div>
      </div>
    </div>
  );
}
